import { useState, type DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useAuth } from "@/auth";
import { useCreateDocument } from "@/hooks/useDocuments";
import { usePlan } from "@/hooks/usePlan";
import { useNoteFirstDocument } from "@/lib/onboarding";
import { ApiRequestError, isPlanLimitCode } from "@/lib/planLimit";
import { requestNewDocument } from "@/components/template/lilia-shell/useGlobalCommandActions";
import { cn } from "@/lib/cn";
import { testIds } from "@/testIds";

const T = testIds.firstRun;

/** "Nadia Osman" → "Nadia"; an email (no name given) → none. */
export function greetingName(userName: string | null | undefined): string | null {
  const name = (userName ?? "").trim();
  if (!name || name.includes("@")) return null;
  return name.split(/\s+/)[0];
}

/**
 * First run 5a (Olivia, 14 Oct 2026): the first screen, and the screen whenever the person owns no
 * documents. Four ways to start; Blank is one click; a file dropped anywhere on it is imported.
 * reports/2026-10-21-welcome-onboarding.md
 */
export function FirstRun() {
  const { t } = useTranslation("shell");
  const { t: tCommon } = useTranslation("common");
  const navigate = useNavigate();
  const { user } = useAuth();
  const plan = usePlan().data;
  const createDocument = useCreateDocument();
  const noteFirstDocument = useNoteFirstDocument();
  const [dragging, setDragging] = useState(false);

  const name = greetingName(user?.userName);
  const planLimit = plan?.enforced ? plan.documents?.limit ?? 0 : 0;
  const limit = planLimit > 0 ? planLimit : null;

  const blank = async () => {
    try {
      const { document } = await createDocument.mutateAsync({ title: tCommon("untitledDocument") });
      noteFirstDocument("blank");
      navigate(`/d/${document.id}`);
    } catch (error) {
      // At the plan's limit the limit dialog explains.
      if (!(error instanceof ApiRequestError && isPlanLimitCode(error.code))) console.error("Failed to create a blank document:", error);
    }
  };

  const importFiles = (files: File[]) => navigate("/import?tab=import", files.length ? { state: { files } } : undefined);

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length) importFiles(files);
  };

  const card = "relative flex flex-col gap-2.5 rounded-[14px] bg-[var(--lilia-surface)] p-5 text-left transition-colors hover:bg-[var(--lilia-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--lilia-primary)] disabled:opacity-60";
  const title = "text-[15.5px] font-semibold text-[var(--lilia-text)]";
  const line = "text-[13px] leading-[1.5] text-[var(--lilia-text-secondary)]";
  const paper = "rounded-[4px] border border-[var(--lilia-border)] bg-[var(--lilia-surface)]";

  return (
    <div
      data-testid={T.root}
      className="relative mx-auto flex w-full max-w-[1000px] flex-col gap-7 py-6"
      onDragOver={(e) => { if (e.dataTransfer.types.includes("Files")) { e.preventDefault(); setDragging(true); } }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setDragging(false); }}
      onDrop={onDrop}
    >
      <div className="flex flex-col gap-2">
        <h1 className="m-0 text-[30px] font-semibold tracking-[-0.02em] text-[var(--lilia-text)]">
          {name ? t("firstRun.greet", { name }) : t("firstRun.greetNoName")}
        </h1>
        <p className="m-0 text-[15px] text-[var(--lilia-text-secondary)]">{t("firstRun.sub")}</p>
      </div>

      <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(200px,1fr))]">
        <button type="button" data-testid={T.blank} onClick={blank} disabled={createDocument.isPending}
          className={cn(card, "border border-[var(--lilia-border-strong)]")}>
          <span aria-hidden className={cn(paper, "h-14 w-11 border-[1.5px] border-[var(--lilia-border-strong)]")} />
          <span className={title}>{createDocument.isPending ? t("firstRun.creating") : t("firstRun.blank")}</span>
          <span className={line}>{t("firstRun.blankDesc")}</span>
        </button>

        <button type="button" data-testid={T.template} onClick={() => requestNewDocument({ view: "template" })}
          className={cn(card, "border-2 border-[var(--lilia-primary)] p-[19px]")}>
          <span aria-hidden className="flex h-14 items-end gap-1.5">
            <span className={cn(paper, "h-[52px] w-10 border-t-[6px] border-t-[var(--lilia-primary)]")} />
            <span className={cn(paper, "h-[52px] w-10")} />
          </span>
          <span className={title}>{t("firstRun.template")}</span>
          <span className={line}>{t("firstRun.templateDesc")}</span>
        </button>

        <button type="button" data-testid={T.import} onClick={() => importFiles([])}
          className={cn(card, "border border-dashed border-[var(--lilia-border-strong)]")}>
          <span aria-hidden className="flex h-14 items-center gap-1.5 font-mono text-[11px] text-[var(--lilia-text-secondary)]">
            {[".tex", ".docx", ".lml"].map((x) => (
              <span key={x} className="rounded-[4px] border border-[var(--lilia-border)] px-1.5 py-[3px]">{x}</span>
            ))}
          </span>
          <span className={title}>{t("firstRun.import")}</span>
          <span className={line}>{t("firstRun.importDesc")}</span>
        </button>

        <button type="button" data-testid={T.describe} onClick={() => navigate("/ai")}
          className={cn(card, "border border-[var(--lilia-border-strong)]")}>
          <span aria-hidden className="flex h-14 items-center">
            <span className="flex h-[30px] items-center rounded-full border border-[var(--lilia-border)] px-2.5 text-[12px] text-[var(--lilia-text-secondary)]">
              {t("firstRun.describeExample")}
            </span>
          </span>
          <span className={title}>{t("firstRun.describe")}</span>
          <span className={line}>{t("firstRun.describeDesc")}</span>
        </button>
      </div>

      {limit != null && (
        <p data-testid={T.plan} className="m-0 text-[13px] text-[var(--lilia-text-secondary)]">
          {t("firstRun.plan", { count: limit })}
        </p>
      )}

      {dragging && (
        <div data-testid={T.dropOverlay}
          className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-[14px] border-2 border-dashed border-[var(--lilia-primary)] bg-[color-mix(in_oklab,var(--lilia-surface)_88%,transparent)] text-[15px] font-semibold text-[var(--lilia-text)]">
          {t("firstRun.dropHere")}
        </div>
      )}
    </div>
  );
}
